import { useState } from 'react';

import { css } from '@emotion/react';
import styled from '@emotion/styled';
import { media } from '@myrealtrip/modash';
import { Text } from '@myrealtrip/web-ui';

import Carousel from '~components/Carousel';
import ContentLoading from '~components/loading/ContentLoading';
import useTransportProducts from '~queries/transportation/pointToPointTicket/useTransportProducts';
import { useDetermineMobile } from '@myrealtrip/device-detect';
import { PASS_RECOMMENDATION_KEY, PASS_RECOMMENDATION_TITLE } from '~constants/pass';
import { classifyProductsByRecommendation } from '~utils/pass/classifiedPassProducts';

import type { PassRecommendationKey } from '~types/pass';

import RecommendationProduct from './PassRecommendation/RecommendationProduct';

const NAVIGATION_HEIGHT_OFFSET = 132;
const PASS_RECOMMENDATION_KEYS: PassRecommendationKey[] = Object.values(PASS_RECOMMENDATION_KEY);

const PassRecommendation = () => {
  const isMobile = useDetermineMobile();
  const { data: transportProducts, isLoading, isError } = useTransportProducts();

  const [hovered, setHovered] = useState<{ [key: string]: boolean }>({});

  const getHideNav = (key: string) => {
    if (isMobile) return true;
    return !hovered[key];
  };

  if (isError) {
    return null;
  }

  if (isLoading || !transportProducts) {
    return <ContentLoading />;
  }

  const productsByRecommendation = classifyProductsByRecommendation(transportProducts);

  return (
    <>
      <Container>
        {PASS_RECOMMENDATION_KEYS.map((key, sectionIndex) => (
          <CarouselContainer key={key}>
            <Title as="h2" typography="headlineBold20">
              {PASS_RECOMMENDATION_TITLE[key]}
            </Title>
            <StyledCarousel
              options={{ dragFree: isMobile, containScroll: 'trimSnaps' }}
              navOptions={{
                hideNav: getHideNav('eurail'),
                heightOffset: NAVIGATION_HEIGHT_OFFSET,
                onMouseEnter: () => setHovered((prev) => ({ ...prev, eurail: true })),
                onMouseLeave: () => setHovered((prev) => ({ ...prev, eurail: false })),
              }}
              isLast={sectionIndex === PASS_RECOMMENDATION_KEYS.length - 1}
            >
              {productsByRecommendation[key].map((product, index) => (
                <RecommendationProduct
                  key={product.gid}
                  product={product}
                  sectionTitle={PASS_RECOMMENDATION_TITLE[key]}
                  sectionHorizontalIndex={sectionIndex}
                  itemIndex={index}
                />
              ))}
            </StyledCarousel>
          </CarouselContainer>
        ))}
      </Container>
    </>
  );
};

export default PassRecommendation;

const Container = styled.div`
  margin: 0 0 40px;

  ${media.minMobile} {
    margin: 0 0 52px;
  }
`;

const Title = styled(Text)`
  padding: 0 20px;
  margin: 20px 0 16px;

  ${media.minMobile} {
    padding: 0 4px;
    margin: 20px 0 4px;
  }
`;

const CarouselContainer = styled.div``;

const StyledCarousel = styled(Carousel)<{ isLast: boolean }>`
  padding: 0 16px 24px;

  ${({ isLast }) =>
    isLast &&
    css`
      padding-bottom: 0;
    `}

  ${media.minMobile} {
    padding: 0;
  }
`;
